import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useSmartPolling } from '@/hooks/useSmartPolling';
import { POLLING_INTERVALS } from '@/lib/performanceConfig';
import { buildLinhasFormatadas, getOperadoraWithFallback, STANDARD_SELECT_FIELDS } from './TriagemStandardColumns';
import { useUserProfileContext } from '@/contexts/UserProfileContext';
import { equipesDoBko } from '@/lib/fila-permissions';
import { equipesDoLider, filtrarPeloLider } from '../triagem/triagemPedidoUtils';
import { depoisDoClique, manterNaTriagem, meuUsuarioId } from '@/components/kanban/triagem/acaoTriagem';

const STATUS_PORTABILIDADE_AGENDADA = '9be22530-0140-4031-9ede-0ba953059aed';
const COLUNA_SMS_ENVIADO = 'SMS ENVIADO';

export interface ItemTriagemPortabilidade {
  item_id: string;
  pedido_id: string;
  numero_pedido: string;
  item_seq: number | null;
  numero_cotacao_operadora: string | null;
  cnpj: string;
  /** CNPJ ou CPF — a fila mostra um segmento por vez (`segmentoTriagem.ts`). */
  tipo_pessoa: string | null;
  /** CNPJ ou CPF, como está no banco. Máscara em `formatarDocumento`. */
  documento: string;
  razao_social: string;
  telefone: string;
  email: string;
  cliente_id: string;
  linhas_telefone: string[];
  linhas_formatadas: string[];
  /** A linha desta PN, crua: a BKO copia e consulta na Claro (Douglas 28/10/2026). */
  ddd: string;
  linha: string;
  operadora_doadora: string;
  plano_nome: string;
  tipo_chip: string;
  faturamento_tipo: string;
  agenda_tipo: string;
  data_agendamento: string;
  contato_real_nome: string;
  cessionario_nome: string;
  cessionario_cpf: string;
  entrada_coluna_em: string;
  status_id: string;
  coluna_atual: string;
  quadro_atual: string;
  is_first_of_cnpj: boolean;
}

export function useTriagemPortabilidade() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useUserProfileContext();
  const { refetchInterval, onQueryError, onQuerySuccess } = useSmartPolling({
    weight: 'B',
    baseInterval: POLLING_INTERVALS.PESO_B,
  });

  const { data: itens = [], isLoading, refetch } = useQuery({
    queryKey: ['triagem-portabilidade', profile?.id, profile?.funcao],
    queryFn: async () => {
      const equipes = await equipesDoBko(profile);
      let q = supabase
        .from('v_planilha_mae')
        .select(STANDARD_SELECT_FIELDS)
        .eq('coluna_atual', 'SMS ENVIADO')
        .eq('quadro_atual', 'PRE_ATIVO');
      if (equipes) q = q.in('equipe_id', equipes);
      q = filtrarPeloLider(q, profile, await equipesDoLider(profile));
      const { data, error } = await q
        .order('manter_em', { ascending: true, nullsFirst: true })
        .order('cnpj', { ascending: true })
        .order('pedido_id', { ascending: true })
        .order('entrada_coluna_em', { ascending: true });

      if (error) {
        onQueryError(error);
        throw error;
      }

      onQuerySuccess();

      // ⛔ Pré-Ativo inteiro DAS SUAS EQUIPES (Douglas 29/09/2026): sem recorte
      // por dono (owner_qualidade_id), mas nunca equipe alheia.
      const filtered = data || [];

      let lastCnpj = '';
      const processed: ItemTriagemPortabilidade[] = (filtered as any[]).map((item: any) => {
        const isFirst = item.cnpj !== lastCnpj;
        lastCnpj = item.cnpj || '';
        return {
          item_id: item.item_id,
          pedido_id: item.pedido_id,
          numero_pedido: String(item.numero_pedido || ''),
          item_seq: item.item_seq ?? null,
          numero_cotacao_operadora: item.numero_cotacao_operadora ?? null,
          cnpj: item.cnpj || '',
          tipo_pessoa: item.tipo_pessoa ?? null,
          documento: item.documento || item.cnpj || '',
          razao_social: item.razao_social || '',
          telefone: item.telefone_principal || '',
          email: item.contato_real_email || '',
          cliente_id: item.cliente_id || '',
          linhas_telefone: buildLinhasFormatadas(item),
          linhas_formatadas: buildLinhasFormatadas(item),
          ddd: item.ddd || '',
          linha: item.linha || '',
          operadora_doadora: getOperadoraWithFallback(item),
          plano_nome: item.plano_nome || '',
          tipo_chip: item.tipo_chip || '',
          faturamento_tipo: item.faturamento_tipo || '',
          agenda_tipo: item.agenda_tipo || '',
          data_agendamento: item.data_agendamento || '',
          contato_real_nome: item.contato_real_nome || '',
          cessionario_nome: item.cessionario_nome || '',
          cessionario_cpf: item.cessionario_cpf || '',
          entrada_coluna_em: item.entrada_coluna_em || '',
          status_id: item.status_id || '',
          coluna_atual: item.coluna_atual || 'SMS ENVIADO',
          quadro_atual: item.quadro_atual || 'PRE_ATIVO',
          is_first_of_cnpj: isFirst,
        };
      });
      return processed;
    },
    refetchInterval,
  });

  // Agendar Portabilidade: uma linha só, com a data, a hora e o status no MESMO
  // PATCH (a data nunca fica de fora do status, nem o status sem a data). O
  // histórico vai em seguida, sem a ida do getUser.
  const agendarPortabilidade = useMutation({
    mutationFn: async ({ itemId, dataAgenda }: { itemId: string; dataAgenda: string }) => {
      const item = itens.find(i => i.item_id === itemId);
      if (!item) throw new Error('Item não encontrado');
      if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dataAgenda)) throw new Error('Informe a data E a hora da portabilidade.');

      const { error } = await supabase
        .from('itens_pedido')
        .update({
          status_id: STATUS_PORTABILIDADE_AGENDADA,
          data_agendamento: dataAgenda + '-03:00',
          agenda_tipo: 'PORTABILIDADE',
        })
        .eq('id', itemId);
      if (error) throw new Error(error.message);

      await supabase.from('historico_status').insert({
        pedido_id: item.pedido_id, item_id: itemId,
        quadro_anterior: item.quadro_atual, quadro_atual: 'PRE_ATIVO',
        coluna_anterior: item.coluna_atual, coluna_atual: 'PORTABILIDADE AGENDADA',
        status_anterior_id: item.status_id, status_atual_id: STATUS_PORTABILIDADE_AGENDADA,
        user_id: await meuUsuarioId(),
        motivo: `Triagem Portabilidade - Agendada para ${dataAgenda}`,
      } as never);

      // ⛔ O cliente NÃO é avisado na hora do agendamento (Douglas 24/09/2026):
      // pode se arrepender e cancelar. Ele recebe a lista das linhas e o horário
      // na véspera (18h–21h) ou no próprio dia, pela conversa do pedido (cron-portabilidade-vespera e -hoje).
      // Até aqui este bloco mandava para um n8n desligado — nunca saiu nada.
      return { pedidoId: item.pedido_id, itemId };
    },
    onSuccess: ({ pedidoId, itemId }) => {
      toast({ title: 'Portabilidade agendada', description: 'O cliente recebe a lista das linhas e o horário na véspera (das 18h às 21h) ou no próprio dia, até 1 h antes.' });
      void depoisDoClique(queryClient, ['triagem-portabilidade', 'triagem-portabilidade-concluida'], { pedidoId, itens: [itemId] }, 'sai');
    },
    onError: (error) => {
      const msg = error instanceof Error ? error.message : '';
      const isAuthError = msg.toLowerCase().includes('refresh_token') || msg.toLowerCase().includes('jwt expired') || msg.toLowerCase().includes('not authenticated');
      toast({ title: isAuthError ? 'Sessão expirada' : 'Erro', description: isAuthError ? 'Sua sessão expirou. Por favor, faça login novamente.' : msg || 'Tente novamente.', variant: 'destructive' });
    },
  });

  // Manter — todos os itens da mesma cotação vão para o fim da fila, numa ida
  // (`fn_triagem_manter`). O PORQUÊ é a justificativa da fila, gravada antes
  // pelo diálogo (vai para a timeline); até 24/09 aqui se gravava "Mantido: "
  // com o texto vazio.
  const manterNoFim = useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      const itemClicado = itens.find(i => i.item_id === itemId);
      if (!itemClicado) throw new Error('Item não encontrado');

      const cotacao = itemClicado.numero_cotacao_operadora;
      const itensMesmaCotacao = cotacao
        ? itens.filter(i => i.numero_cotacao_operadora === cotacao)
        : [itemClicado];

      const ids = itensMesmaCotacao.map(i => i.item_id);
      const r = await manterNaTriagem({ pedidoId: itemClicado.pedido_id, itens: ids, coluna: COLUNA_SMS_ENVIADO });
      return { count: r.mantidos, pedidoId: itemClicado.pedido_id, ids };
    },
    onSuccess: ({ count, pedidoId, ids }) => {
      toast({ title: 'Mantido', description: count > 1 ? `${count} itens da mesma cotação foram para o fim da fila.` : 'Item foi para o fim da fila.' });
      void depoisDoClique(queryClient, ['triagem-portabilidade'], { pedidoId, itens: ids }, 'desce');
    },
    onError: (error) => {
      toast({ title: 'Erro', description: error instanceof Error ? error.message : 'Tente novamente.', variant: 'destructive' });
    },
  });

  return {
    itens, isLoading, refetch,
    agendarPortabilidade: agendarPortabilidade.mutateAsync,
    manterNoFim: manterNoFim.mutate,
    isAgendando: agendarPortabilidade.isPending,
    isMantendo: manterNoFim.isPending,
  };
}
